import { useEffect } from "react";
import { useRouteError } from "react-router";
import { AccessDeniedPage } from "../ErrorPage/AccessDeniedPage";
import { GonePage } from "../ErrorPage/GonePage";
import { NotFoundPage } from "../ErrorPage/NotFoundPage";
import { RouteCrashPage } from "../ErrorPage/RouteCrashPage";
import { reportRouteBoundaryError } from "../reportRouteBoundaryError";
import { classifyRouteError } from "./classifyRouteError";
import type { RouteErrorComponents } from "./types";

export type RouteBoundaryErrorContext = Readonly<{
  boundary: string;
}>;

export type RouteBoundaryErrorReporter = (
  error: unknown,
  context: RouteBoundaryErrorContext,
) => void;

export type RouteErrorBoundaryProps = Readonly<{
  boundaryName: string;
  components?: Partial<RouteErrorComponents>;
  onReset?(): void;
  reportError?: RouteBoundaryErrorReporter;
}>;

const DEFAULT_COMPONENTS: RouteErrorComponents = {
  // Shown briefly while a login redirect happens; apps can override with a spinner.
  LoginRequired: () => null,
  AccessDenied: AccessDeniedPage,
  NotFound: NotFoundPage,
  Gone: GonePage,
  Crash: RouteCrashPage,
};

export function RouteErrorBoundary({
  boundaryName,
  components,
  onReset,
  reportError = reportRouteBoundaryError,
}: RouteErrorBoundaryProps) {
  const error = useRouteError();
  const classification = classifyRouteError(error);
  const isCrash = classification.type === "crash";

  useEffect(() => {
    if (isCrash) {
      reportError(error, { boundary: boundaryName });
    }
  }, [boundaryName, error, isCrash, reportError]);

  const { LoginRequired, AccessDenied, NotFound, Gone, Crash } = {
    ...DEFAULT_COMPONENTS,
    ...components,
  };

  switch (classification.type) {
    case "loginRequired":
      return <LoginRequired />;
    case "accessDenied":
      return <AccessDenied {...classification.data} />;
    case "notFound":
      return <NotFound />;
    case "gone":
      return <Gone message={classification.message} />;
    case "crash":
      return <Crash error={error} reset={onReset ?? (() => window.location.reload())} />;
  }
}
